import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { useProductGroupKpiTotals } from "@acto/taskpane/core/data-access/customers/useProductGroupKpiTotals";
import { Kpi, KpiComparisonMode } from "@acto/taskpane/core/domain";
import { CHART_COLORS } from "../productDrill";
import { useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { Divider, Flex, SectionDivider } from "../../../primitives";
import { Description, MetricFigures, ProductRowList, SidePanelContainer, type ProductRowView } from "../../ui";
import { ProductGroupHeader } from "./ProductGroupHeader";
import { CustomerMetricSwitch } from "../../CustomerDetailsSidePanel/ui/CustomerMetricSwitch";
import { CustomerMetricsSkeleton } from "../../CustomerDetailsSidePanel/ui/CustomerMetricsSkeleton";
import {
  type CustomerMetric,
  METRIC_KPI_TYPE,
  METRIC_PRODUCT_KPI,
  metricTotalKpis,
  formatMetricValue,
  useMetricLabels,
} from "../../CustomerDetailsSidePanel/customerMetric";
import { type DateRange, PeriodOption } from "../../../DateRangeSelector/types";
import { isComparableWindow, periodToDateRange } from "../../../../utils/periodToDateRange";
import { formatComparisonRange } from "../../../../utils/dateFormatters";
import type { ProductGroupRef } from "../productDrill";

const EmptyState = styled("div")(({ theme }) => ({
  color: theme.managementView.colors.neutral[56],
  fontSize: theme.managementView.font.size[14],
}));

interface ProductDrillLevelProps {
  clientId: string;
  customerName: string;
  node: ProductGroupRef;
  metric: CustomerMetric;
  onMetricChange: (metric: CustomerMetric) => void;
  onOpen: (group: ProductGroupRef) => void;
  selectedPeriod: PeriodOption;
  selectedCustomDateRange?: DateRange;
}

export const ProductDrillLevel = ({
  clientId,
  customerName,
  node,
  metric,
  onMetricChange,
  onOpen,
  selectedPeriod,
  selectedCustomDateRange,
}: ProductDrillLevelProps) => {
  const t = useTranslations();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const metricLabels = useMetricLabels();
  const tenantComparisonMode = useComparisonModeContext();
  const comparisonMode = toGqlKpiComparisonMode(tenantComparisonMode);

  const dateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const showTrends = isComparableWindow(selectedPeriod, selectedCustomDateRange);
  const rangeLabel = formatComparisonRange(dateRange, locale);
  const metricKpi = Kpi[METRIC_KPI_TYPE[metric]];

  const { totals, isLoading: isLoadingTotals } = useProductGroupKpiTotals({
    clientId,
    productGroupId: node.id,
    kpis: metricTotalKpis(metric),
    dateRange,
    comparisonMode,
  });

  const children = useProductGroups({
    clientId,
    parents: [node.id],
    dateRange,
    selectedKpi: METRIC_PRODUCT_KPI[metric],
    comparisonMode,
  });

  const total = totals.get(metricKpi);
  const revenue = totals.get(Kpi.REVENUE);
  const comparisonLabel =
    tenantComparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("kpi.previousPeriod") : t("kpi.previousYear");

  const rows: ProductRowView[] = children.items.map((item, index) => ({
    key: item.id,
    name: item.name,
    externalReference: item.externalReference,
    formattedValue: item.formattedValue ?? "-",
    change: showTrends ? item.change : null,
    color: CHART_COLORS[index % CHART_COLORS.length],
    onOpen: item.hasChildren
      ? () => onOpen({ id: item.id, name: item.name, externalReference: item.externalReference })
      : undefined,
  }));

  return (
    <SidePanelContainer>
      <Flex direction="column" gap={24}>
        <ProductGroupHeader
          name={node.name}
          subtitle={[node.externalReference, customerName].filter(Boolean).join(" · ")}
        />

        <Flex direction="column" gap={16}>
          <Divider />
          <CustomerMetricSwitch value={metric} onChange={onMetricChange} />
          <Divider />
        </Flex>

        {isLoadingTotals ? (
          <CustomerMetricsSkeleton />
        ) : total ? (
          <MetricFigures
            label={`${metricLabels[metric].label} (${rangeLabel})`}
            value={formatMetricValue(metric, total.value, revenue?.value ?? null, locale, currency)}
            comparison={
              showTrends && total.comparison
                ? {
                    label: `${comparisonLabel} (${formatComparisonRange(total.comparison.range, locale)})`,
                    value: formatMetricValue(
                      metric,
                      total.comparison.value,
                      revenue?.comparison?.value ?? null,
                      locale,
                      currency,
                    ),
                    percentageChange: total.comparison.percentageChange ?? 0,
                  }
                : undefined
            }
          />
        ) : (
          <EmptyState>{t("managementView.customerDetails.noDataAvailable")}</EmptyState>
        )}

        <Divider />

        <Flex direction="column" gap={12}>
          <Description>{`${t("managementView.productGroup.breakdown")} (${rangeLabel})`}</Description>
          {!children.isPending && rows.length === 0 ? (
            <EmptyState>{t("managementView.productGroup.noProducts")}</EmptyState>
          ) : (
            <ProductRowList rows={rows} isLoadingMore={children.isPending || children.isLoadingMore} />
          )}
          {children.hasMore && !children.isLoadingMore && <SectionDivider onClick={children.loadMore} />}
        </Flex>
      </Flex>
    </SidePanelContainer>
  );
};
